/**
 * Machine page › Energy.
 *
 * The demo's `machineTab('energy')` (line 2224): four tiles — drive and its
 * nominal draw, diesel and electricity over the 12 days with the site's
 * prices, and emissions intensity against the class peers — then diesel and
 * electricity per day, and CO₂-e per day.
 *
 * Emissions factors are the site's (`ENERGY_FACTORS`, eager): diesel 2.68 kg
 * per litre (scope 1) plus the site's grid factor per kWh (scope 2).
 *
 * Domain: `telemetry` (`energy` + `shift_daily`).
 *
 * UI layer.
 *
 * @see src/versions/v1/modules/fleet/MachineScene.transform.ts
 * @see docs/logic/fleet/machine-page.md
 */

import { useMemo } from 'react';
import { unpackTable } from '../../shared/columnar';
import { fmt$, groupNum } from '../../shared/oem-engine';
import { emissionsIntensityCalc } from '../../shared/source-calc';
import { useOemData, type ShiftDailyRow } from '../../shared/useOemData';
import { LineChart } from '../../shared/viz/LineChart';
import { useSourceDrawer } from '../issues/useSourceDrawer';
import { TabLoader } from './TabLoader';
import { machineEnergy } from './MachineScene.transform';

export interface MachineEnergyTabProps {
  machineId: string;
  mineName: string;
  cls: string;
}

const CARD =
  'rounded-2xl border border-[var(--glass-border)] bg-[var(--glass-surface)] p-4 backdrop-blur-lg';

export function MachineEnergyTab({ machineId, mineName, cls }: MachineEnergyTabProps) {
  const { data, loading, error } = useOemData('telemetry');
  const { openDataset } = useSourceDrawer();

  const view = useMemo(
    () =>
      data
        ? machineEnergy(machineId, data.energy, unpackTable<ShiftDailyRow>(data.shift_daily))
        : null,
    [data, machineId],
  );

  return (
    <TabLoader loading={loading} error={error} what="Energy data" rows={8}>
      {view ? (
        <div className="@container space-y-6">
          <div className="grid grid-cols-1 gap-3 @lg:grid-cols-2 @4xl:grid-cols-4">
            <Tile label="Drive" value={view.drive} small detail={view.nominal} />
            <Tile
              label="Diesel · 12 days"
              value={groupNum(view.dieselL)}
              unit="litres"
              detail={`A$${view.dieselPrice}/L at ${mineName}`}
            />
            <Tile
              label="Electricity · 12 days"
              value={groupNum(view.kwh)}
              unit="kWh"
              detail={`${view.powerSource} · A$${view.powerPrice}/kWh`}
            />
            <Tile
              label="Emissions intensity"
              value={String(view.co2PerHour)}
              unit="kg CO₂-e / op h"
              onOpen={() =>
                openDataset('shift', {
                  title: `Shift records · ${machineId}`,
                  filter: { machine_id: machineId },
                  calc: emissionsIntensityCalc(
                    {
                      diesel_l: view.dieselL,
                      energy_kwh: view.kwh,
                      hours: view.hours,
                      co2_kg_per_hour: view.co2PerHour,
                    },
                    view.gridFactor,
                  ),
                })
              }
              detail={`${cls} peers: ${view.peerCo2PerHour.toFixed(1)} kg/h · energy cost ${fmt$(view.energyCost)} / 12 d`}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 @3xl:grid-cols-2">
            <section className={CARD}>
              <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                Diesel and electricity per day
              </h3>
              <p className="mb-2 mt-0.5 text-[11px] text-muted">
                From shift records · {view.labels.length} days
              </p>
              <LineChart
                labels={view.labels}
                series={[
                  { values: view.dieselPerDay, color: 'var(--warn)', name: 'diesel L', dots: true },
                  {
                    values: view.kwhPerDay,
                    color: 'var(--info)',
                    name: 'electricity kWh',
                    dots: true,
                  },
                ]}
                min={0}
                xTitle="Date (2026)"
                yTitle="Litres · kWh per day"
              />
            </section>
            <section className={CARD}>
              <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                CO₂-e per day
              </h3>
              <p className="mb-2 mt-0.5 text-[11px] text-muted">
                Diesel 2.68 kg/L (scope 1) + grid {view.gridFactor} kg/kWh (scope 2)
              </p>
              <LineChart
                labels={view.labels}
                series={[
                  { values: view.co2PerDay, color: 'var(--accent)', area: true, dots: true },
                ]}
                min={0}
                xTitle="Date (2026)"
                yTitle="kg CO₂-e per day"
              />
            </section>
          </div>

          <p className="text-[11px] text-muted">{view.calc}.</p>
        </div>
      ) : (
        <p className="text-sm text-muted">No energy record for {machineId}.</p>
      )}
    </TabLoader>
  );
}

function Tile({
  label,
  value,
  unit,
  detail,
  small,
  onOpen,
}: {
  label: string;
  value: string;
  unit?: string;
  detail: string;
  small?: boolean;
  /** Makes the figure a source link — the demo's dotted `FIG(…)`. */
  onOpen?: () => void;
}) {
  return (
    <div className={CARD}>
      <p className="text-xs uppercase tracking-wider text-muted">{label}</p>
      <p
        className={`mt-1 font-semibold text-zinc-900 dark:text-zinc-100 ${small ? 'text-base' : 'text-2xl'}`}
      >
        {onOpen ? (
          <button
            type="button"
            onClick={onOpen}
            title="Open the rows behind this figure"
            className="rounded underline decoration-dotted decoration-1 underline-offset-4 hover:decoration-solid focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--accent)_55%,transparent)]"
          >
            {value}
          </button>
        ) : (
          value
        )}
        {unit ? <span className="ml-1 text-xs font-normal text-muted">{unit}</span> : null}
      </p>
      <p className="mt-1 text-[11px] text-muted">{detail}</p>
    </div>
  );
}
